"use client";

import React, { useEffect, useState } from "react";
import { Laptop, Smartphone, Shield, X, Trash2, Loader2, CheckCircle2, Info } from "lucide-react";
import { useToast } from "../ui/Toast";
import { useDialog } from "../ui/Dialog";
import { useTranslation } from "@/lib/i18n/context";

interface SessionItem {
  id: string;
  isCurrent: boolean;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  lastActiveAt: string;
}

interface ActiveSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActiveSessionsModal({ isOpen, onClose }: ActiveSessionsModalProps) {
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const toast = useToast();
  const dialog = useDialog();
  const { t } = useTranslation();

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/auth/sessions");
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      void fetchSessions();
    }
  }, [isOpen]);

  const handleRevokeSingle = async (session: SessionItem) => {
    const confirmed = await dialog.confirm({
      title: t("auth.sessions.removeSession"),
      message: session.isCurrent
        ? "Terminating your current session will sign you out of this browser. Your user account will not be deleted."
        : t("auth.sessions.removeSessionConfirm"),
      confirmText: t("auth.sessions.removeSession"),
      variant: "danger",
    });
    if (!confirmed) return;

    setRevokingSessionId(session.id);
    try {
      const res = await fetch(`/api/auth/sessions/${session.id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success(t("auth.sessions.sessionRevoked"), "The user account remains active.");
        if (session.isCurrent) {
          window.location.href = "/login";
          return;
        }
        await fetchSessions();
      } else {
        toast.error("Error", "Failed to revoke session");
      }
    } catch {
      toast.error("Network error revoking session");
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRevokeOthers = async () => {
    const confirmed = await dialog.confirm({
      title: t("auth.sessions.revokeAll"),
      message: "Are you sure you want to revoke all other active sessions? All other logged-in devices will be signed out. Your user account will not be deleted.",
      confirmText: t("auth.sessions.revokeAll"),
      variant: "danger",
    });
    if (!confirmed) return;

    setRevoking(true);
    try {
      const res = await fetch("/api/auth/sessions", { method: "DELETE" });
      if (res.ok) {
        toast.success("Sessions Revoked", "All other active sessions have been terminated. Your account remains active.");
        await fetchSessions();
      } else {
        toast.error("Error", "Failed to revoke sessions");
      }
    } catch {
      toast.error("Network error revoking sessions");
    } finally {
      setRevoking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-surface-overlay backdrop-blur-sm animate-fade-in">
      <div className="bg-surface-card border border-themed-border rounded-xl max-w-lg w-full p-6 shadow-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-themed-textSecondary hover:text-themed-textPrimary p-1 rounded-lg hover:bg-surface-muted"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-lg bg-status-info-bg border border-blue-500/20 text-[var(--accent-primary)]">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-themed-textPrimary">{t("auth.sessions.title")}</h3>
            <p className="text-xs text-themed-textSecondary">{t("auth.sessions.subtitle")}</p>
          </div>
        </div>

        <div className="mb-4 rounded-xl border border-status-info-border bg-status-info-bg/40 p-3 text-xs text-themed-textSecondary flex items-start gap-2.5">
          <Info className="w-4 h-4 text-themed-accent shrink-0 mt-0.5" />
          <span>{t("auth.sessions.notDeleteUserNotice")}</span>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--accent-primary)]" />
            <span className="text-xs text-themed-textSecondary">Loading active sessions...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {sessions.length === 0 ? (
                <p className="text-xs text-themed-textMuted py-4 text-center">{t("auth.sessions.noOtherSessions")}</p>
              ) : (
                sessions.map((session) => {
                  const isRevokingThis = revokingSessionId === session.id;
                  return (
                    <div
                      key={session.id}
                      className={`p-3.5 rounded-xl border flex items-start justify-between gap-3 ${
                        session.isCurrent
                          ? "bg-[var(--accent-primary)]/10 border-[var(--border-accent)]"
                          : "bg-surface-root/60 border-themed-border hover:bg-surface-root"
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="p-2 rounded-lg bg-surface-muted text-themed-textSecondary mt-0.5 shrink-0">
                          {session.userAgent.includes("Mobile") || session.userAgent.includes("Android") || session.userAgent.includes("iPhone") ? (
                            <Smartphone className="w-4 h-4" />
                          ) : (
                            <Laptop className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono font-bold text-themed-textPrimary">
                              {session.ipAddress}
                            </span>
                            {session.isCurrent && (
                              <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border border-[var(--border-accent)]">
                                <CheckCircle2 className="w-3 h-3" /> {t("auth.sessions.currentSession")}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-themed-textSecondary font-mono mt-0.5 truncate max-w-sm" title={session.userAgent}>
                            {session.userAgent}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-themed-textMuted flex-wrap">
                            <span>Started: {new Date(session.createdAt).toLocaleString()}</span>
                            <span>Last active: {new Date(session.lastActiveAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => void handleRevokeSingle(session)}
                        disabled={isRevokingThis || revoking}
                        className="p-1.5 rounded-lg text-themed-textMuted hover:text-status-danger hover:bg-status-danger-bg transition-colors shrink-0 disabled:opacity-50"
                        title={t("auth.sessions.removeSession")}
                      >
                        {isRevokingThis ? (
                          <Loader2 className="w-4 h-4 animate-spin text-status-danger" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-themed-border">
              <button
                type="button"
                onClick={handleRevokeOthers}
                disabled={revoking || sessions.filter((s) => !s.isCurrent).length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-status-danger-bg hover:bg-status-danger-bg text-status-danger border border-status-danger-border text-xs font-medium transition-colors disabled:opacity-40"
              >
                {revoking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                <span>{revoking ? t("auth.sessions.revoking") : t("auth.sessions.revokeAll")}</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                className="px-4 py-1.5 rounded-lg bg-surface-muted hover:bg-surface-muted text-themed-textSecondary text-xs font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
